// ---------------------------------------------------------------------------
// Masking utility
// ---------------------------------------------------------------------------

import { isValidEmail, safeString, isValidPhone } from "./utils";

/**
 * Takes a raw email or phone string and returns a masked version.
 *
 * Email  →  "jo**@gm***.com"
 * Phone  →  "+234 *** *** 7890"  (last 4 digits always visible)
 *
 * Returns an empty string when the input is empty/null/undefined.
 */
export function maskContactInfo(value: string | null | undefined): string {
  const raw = (value ?? "").trim();
  if (!raw) return "";

  // ── Email ──────────────────────────────────────────────────────────────
  if (raw.includes("@")) {
    const [local, domain] = raw.split("@");
    if (!domain) return raw; // malformed — return as-is

    // Mask local part: keep first 2 chars, replace the rest with *
    const maskedLocal =
      local.length <= 2
        ? local.replace(/./g, "*")
        : local.slice(0, 2) + "*".repeat(Math.min(local.length - 2, 4));

    // Mask domain: keep TLD, replace middle chars with *
    const dotIdx = domain.lastIndexOf(".");
    if (dotIdx <= 0) return `${maskedLocal}@${domain}`;
    const domainName = domain.slice(0, dotIdx);
    const tld = domain.slice(dotIdx); // e.g. ".com"
    const maskedDomain =
      domainName.length <= 1
        ? domainName
        : domainName[0] + "*".repeat(Math.min(domainName.length - 1, 3));

    return `${maskedLocal}@${maskedDomain}${tld}`;
  }

  // ── Phone ──────────────────────────────────────────────────────────────
  // Strip everything except digits and a leading +
  const prefix = raw.startsWith("+") ? "+" : "";
  const digits = raw.replace(/\D/g, "");

  if (digits.length < 4) return raw; // too short to mask sensibly

  const visible = digits.slice(-4);
  const maskedCount = digits.length - 4;
  const masked = "*".repeat(maskedCount);

  return `${prefix}${masked}${visible}`;
}

// ---------------------------------------------------------------------------
// Contact availability helpers
// ---------------------------------------------------------------------------

/**
 * Returns an error message when the raw contact value for a given mode is
 * missing or invalid, so the UI can block that option.
 *
 * Validates against the raw `email` / `phoneNumber` from the store —
 * NOT the masked values from the contact endpoint, which are display-only.
 *
 * Returns `null` when the contact value is healthy.
 */
export function getContactAvailabilityError(
  mode: string,
  email: string | null | undefined,
  phoneNumber: string | null | undefined,
  hasMultipleModes: boolean,
): string | null {
  const chooseAnother = hasMultipleModes
    ? " choose another option or contact your provider."
    : " contact your provider.";

  if (mode === "email") {
    if (!isValidEmail(email)) {
      return `There is no email attached to your account,${chooseAnother}`;
    }
    return null;
  }

  // sms | whatsapp
  if (!isValidPhone(phoneNumber)) {
    return `There is no phone number attached to your account,${chooseAnother}`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Validation helpers
// ---------------------------------------------------------------------------

export function matchesInput(input: string, value: unknown): boolean {
  try {
    const safeValue = safeString(value).toLowerCase();
    const safeInput = safeString(input).toLowerCase();
    return safeValue === safeInput;
  } catch {
    return false;
  }
}
